import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import styles from "./PartnerDetail.module.sass";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";

// Screens
import NameAndDescription from "./NameAndDescription";
import CandidateList from "./Candidate&List";

// Components
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import Actions from "../../components/Actions";

// Services
import { getOfferById, getCommercials, assignCommercialToOffer, updateOffer } from "../../services/offerAdmin.service";
import { showSuccess, showError } from "../../services/toast.service";

const navigation = ["Détails de l'offre", "Candidatures"];

const getStatusClass = (status) => {
  switch (status) {
    case "active":
      return "status-green";
    case "closed":
      return "status-red";
    case "draft":
      return "status-yellow";
    default:
      return "status-gray";
  } 
};

const formatStatus = (status) => {
  switch (status) {
    case "active":
      return "Active";
    case "closed":
      return "Fermée";
    case "draft":
      return "Brouillon";
    default:
      return status;
  }
};

const AlternanceOfferDetail = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true); // 👈 Ajout de l'état de chargement
  const [error, setError] = useState(null); // 👈 Ajout de l'état d'erreur
  const [commercials, setCommercials] = useState([]);
  const [assigningCommercial, setAssigningCommercial] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const location = useLocation();
  const { id } = useParams();

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(date);
    } catch (e) {
      return dateString;
    }
  }, []);

  const refreshOffer = useCallback(async () => {
    try {
      if (id) {
        console.log('RefreshOffer - Récupération de l\'offre avec ID:', id);
        const result = await getOfferById(id);
        if (result.success && result.data) {
          console.log('RefreshOffer - Données reçues:', result.data);
          console.log('RefreshOffer - assigned_to:', result.data.assigned_to);
          console.log('RefreshOffer - expand.assigned_to:', result.data.expand?.assigned_to);
          setOffer(result.data);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'offre:', error);
    }
  }, [id]);

  const assignCommercial = async (commercialId) => {
    try {
      setAssigningCommercial(true);
      const result = await assignCommercialToOffer(offer.id, commercialId);
      
      if (result.success) {
        await refreshOffer();
        showSuccess('Commercial assigné avec succès à l\'offre');
      } else {
        showError('Erreur lors de l\'assignation du commercial: ' + result.error);
      }
      setAssigningCommercial(false);
    } catch (error) {
      console.error('Erreur lors de l\'assignation du commercial:', error);
      showError('Erreur lors de l\'assignation du commercial');
      setAssigningCommercial(false);
    }
  };

  // Fonction pour changer le statut de l'offre
  const handleStatusChange = async () => {
    if (!offer) return;
    
    try {
      setUpdatingStatus(true);
      const newStatus = offer.status === 'Active' ? 'Inactive' : 'Active';
      const actionText = newStatus === 'Active' ? 'activée' : 'désactivée';
      
      const result = await updateOffer(offer.id, { status: newStatus });
      
      if (result.success) {
        await refreshOffer();
        showSuccess(`Offre ${actionText} avec succès!`);
      } else {
        showError(result.error || 'Erreur lors du changement de statut');
      }
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      showError(error.message || 'Erreur lors du changement de statut');
    } finally {
      setUpdatingStatus(false);
    }
  };

  useEffect(() => {
    // Si les données sont déjà passées via l'état de la route, on les utilise directement
    if (location.state?.offer) {
      console.log("Données depuis location.state:", location.state.offer);
      console.log("location.state assigned_to:", location.state.offer.assigned_to);
      console.log("location.state expand:", location.state.offer.expand);
      setOffer(location.state.offer);
      setLoading(false); // L'offre est déjà là, donc on n'est plus en chargement
      return;
    }

    // Sinon, on charge les données depuis l'API
    const loadOffer = async () => {
      try {
        setLoading(true); // Démarrage du chargement
        setError(null); // Réinitialisation des erreurs
        console.log("AlternanceOfferDetail - Chargement de l'offre avec ID:", id);
        const result = await getOfferById(id);

        console.log("coucou", result);

        if (result.success && result.data) {
          console.log("AlternanceOfferDetail - Données reçues:", result.data);
          console.log("AlternanceOfferDetail - assigned_to:", result.data.assigned_to);
          console.log("AlternanceOfferDetail - expand:", result.data.expand);
          console.log("AlternanceOfferDetail - expand.assigned_to:", result.data.expand?.assigned_to);
          setOffer(result.data);
        } else {
          console.error("Erreur lors de la récupération de l'offre:", result.error);
          setError(result.error || "Une erreur est survenue.");
        }
      } catch (err) {
        console.error("Erreur:", err);
        setError("Erreur lors de la récupération de l'offre.");
      } finally {
        setLoading(false); // Fin du chargement, que ce soit un succès ou un échec
      }
    };
    loadOffer();
  }, [id, location.state]);

  useEffect(() => {
    const fetchCommercials = async () => {
      try {
        const commercialsData = await getCommercials();
        setCommercials(commercialsData);
      } catch (error) {
        console.error('Erreur lors de la récupération des commerciaux:', error);
      }
    };

    fetchCommercials();
  }, []);

  // Calculer le nombre de candidatures
  const candidatesCount = offer?.candidats_ids?.length || 0;

  // Affichage conditionnel basé sur l'état de chargement et d'erreur
  if (loading) {
    return (
      <div className={cn(styles.shop, styles.centerContent)}>
        <p>Chargement des détails de l'offre...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(styles.shop, styles.centerContent)}>
        <p className={styles.errorMessage}>Erreur : {error}</p>
        <Link className={cn("button-white", styles.button)} to="/admin/alternance-offers">
          <Icon name="arrow-left" />
          Retour
        </Link>
      </div>
    );
  }

  // Le reste du code ne s'exécute que si l'offre a été chargée
  return (
    <>
      <div className={styles.shop}>
        <Link className={cn("button-white", styles.button)} to="/admin/alternance-offers">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <Card className={styles.card}>
          <div className={styles.profile}>
            <div className={styles.details}>
              <div className={styles.avatar}>
                <img src="/images/content/convention_logo.png" alt="Icône convention" />
              </div>
              <div className={styles.wrap}>
                <div className={cn("h4", styles.man)}>
                  {offer.title || "Titre non défini"}
                </div>
                <div className={styles.login}>
                  {offer.sector_activity && offer.job_location
                    ? `${offer.sector_activity} • ${offer.job_location}`
                    : offer.sector_activity || offer.job_location || "Secteur et lieu non définis"}
                </div>
                <div className={styles.date}>
                  Créée le {formatDate(offer.created)}
                </div>
              </div>
            </div>

            <div className={styles.statusContainer}>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Assigné à</div>
                {offer.expand?.assigned_to ? (
                  <div className={cn(styles.statusBadge, 'status-type', 'status-blue-dark')}>
                    {offer.expand.assigned_to.name}
                  </div>
                ) : assigningCommercial ? (
                  <div className={cn(styles.statusBadge, 'status-type', 'status-blue-dark')}>
                    Assignation...
                  </div>
                ) : (
                  <Dropdown
                    value="Assigner un commercial"
                    options={commercials.map(commercial => commercial.name)}
                    setValue={(selectedName) => {
                      const selectedCommercial = commercials.find(c => c.name === selectedName);
                      if (selectedCommercial) {
                        assignCommercial(selectedCommercial.id);
                      }
                    }}
                  />
                )}
              </div>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Statut</div>
                <div className={cn(styles.statusBadge, "status-type", getStatusClass(offer.status))}>
                  {formatStatus(offer.status)}
                </div>
              </div>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Candidatures</div>
                <div className={cn(styles.statusBadge, "status-type", "status-blue-dark")}>
                  {candidatesCount} candidat{candidatesCount > 1 ? "s" : ""}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.control}>
            <div className={cn(styles.nav, styles.desktopNav)}>
              {navigation.map((x, index) => (
                <button
                  className={cn(styles.link, {
                    [styles.active]: index === activeIndex,
                  })}
                  onClick={() => setActiveIndex(index)}
                  key={index}
                >
                  {x}
                </button>
              ))}
            </div>
            
            <div className={styles.actionContainer}>
              {updatingStatus ? (
                <div className={styles.loading}>Mise à jour en cours...</div>
              ) : (
                <Actions
                  className={styles.actions}
                  classActionsHead={styles.actionsHead}
                  items={[
                    {
                      title: offer?.status === 'Active' ? 'Désactiver l\'offre' : 'Activer l\'offre',
                      action: handleStatusChange
                    }
                  ]}
                  small
                />
              )}
            </div>

            <div className={styles.mobileNav}>
              <Dropdown
                className={styles.dropdown}
                value={navigation[activeIndex]}
                setValue={(value) => {
                  const index = navigation.findIndex((x) => x === value);
                  if (index !== -1) setActiveIndex(index);
                }}
                options={navigation}
              />
            </div>
          </div>
        </Card>

        <div className={styles.wrap}>
          {activeIndex === 0 && <NameAndDescription offer={offer} />}
          {activeIndex === 1 && <CandidateList offer={offer} />}
        </div>
      </div>
    </>
  );
};

export default AlternanceOfferDetail;